import React, { Suspense, lazy, useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { auth } from "./firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import axios from "axios";
import config from "./config";
import { PageSkeleton } from "./components/LoadingSkeleton";

// Lazy load components for code splitting
const HomePage = lazy(() => import("./components/HomePage"));
const PatientLogin = lazy(() => import("./components/PatientLogin"));
const Dashboard = lazy(() => import("./components/ProviderDashboard"));
const PatientForm = lazy(() => import("./components/PatientForm"));
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
const DoctorDashboard = lazy(() => import("./components/DoctorDashboard"));
const ProviderDashboardSimple = lazy(() => import("./components/ProviderDashboardSimple"));
const AppointmentBooking = lazy(() => import("./components/AppointmentBooking"));
const DoctorSignup = lazy(() => import("./components/DoctorSignup"));
const DoctorOnboardingForm = lazy(() => import("./components/DoctorOnboardingForm"));
const DoctorList = lazy(() => import("./components/DoctorList"));
const PatientLandingPage = lazy(() => import("./components/PatientLandingPage"));
const DoctorLandingPage = lazy(() => import("./components/DoctorLandingPage"));
const MedicalRecordsFeature = lazy(() => import("./components/features/MedicalRecordsFeature"));
const AiInsightsFeature = lazy(() => import("./components/features/AiInsightsFeature"));
const DigitalTwinFeature = lazy(() => import("./components/features/DigitalTwinFeature"));
const DoctorAiAssistantFeature = lazy(() => import("./components/features/DoctorAiAssistantFeature"));
const WearableIntegrationFeature = lazy(() => import("./components/features/WearableIntegrationFeature"));
const HelpCenterPage = lazy(() => import("./components/HelpCenterPage"));
const ApiDocsPage = lazy(() => import("./components/ApiDocsPage"));
const AdminLogin = lazy(() => import("./components/AdminLogin"));

// Loading component
function LoadingFallback() {
  return <PageSkeleton />;
}

function PrivateRoute({ children }) {
  const [authLoading, setAuthLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Show loading while checking auth state
  if (authLoading) {
    return <LoadingFallback />;
  }

  return isAuthenticated ? children : <Navigate to="/patient-login" />;
}

function DoctorOnboardingRoute({ children }) {
  const [authLoading, setAuthLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const evaluateOnboarding = async () => {
        if (user) {
          setIsAuthenticated(true);

          // Check if doctor profile exists and onboarding is complete
          try {
            const response = await axios.get(`${config.apiUrl}/api/doctors/${user.uid}`);
            if (response.data.exists && response.data.data.onboardingCompleted) {
              setNeedsOnboarding(false);
            } else {
              setNeedsOnboarding(true);
            }
          } catch (error) {
            // No profile exists, needs onboarding
            setNeedsOnboarding(true);
          } finally {
            setAuthLoading(false);
          }
        } else {
          setIsAuthenticated(false);
          setAuthLoading(false);
        }
      };

      evaluateOnboarding();
    });

    return () => unsubscribe();
  }, []);

  // Show loading while checking auth state
  if (authLoading) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/patient-login" />;
  }

  if (!needsOnboarding) {
    return <Navigate to="/doctor-dashboard" />;
  }

  return children;
}

function DoctorPrivateRoute({ children }) {
  const [authLoading, setAuthLoading] = useState(true);
  const [isDoctorAuthenticated, setIsDoctorAuthenticated] = useState(false);
  const [redirectPath, setRedirectPath] = useState("/patient-login");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setIsDoctorAuthenticated(false);
        setRedirectPath("/patient-login");
        setAuthLoading(false);
        return;
      }

      const evaluateDoctorAccess = async () => {
        try {
          const response = await axios.get(`${config.apiUrl}/api/doctors/${user.uid}`);
          const doctorData = response.data?.data;
          if (response.data?.exists && doctorData?.onboardingCompleted) {
            if (doctorData.verificationStatus === "approved" || doctorData.verificationStatus === "pending") {
              setIsDoctorAuthenticated(true);
              return;
            }
            setIsDoctorAuthenticated(false);
            setRedirectPath("/doctor-onboarding");
          } else {
            setIsDoctorAuthenticated(false);
            setRedirectPath("/doctor-onboarding");
          }
        } catch (error) {
          setIsDoctorAuthenticated(false);
          setRedirectPath("/doctor-onboarding");
        } finally {
          setAuthLoading(false);
        }
      };

      evaluateDoctorAccess();
    });

    return () => unsubscribe();
  }, []);

  if (authLoading) {
    return <LoadingFallback />;
  }

  return isDoctorAuthenticated ? children : <Navigate to={redirectPath} />;
}

export default function App() {
  return (
    <Router>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/patient-login" element={<PatientLogin />} />
          <Route path="/for-patients" element={<PatientLandingPage />} />
          <Route path="/for-doctors" element={<DoctorLandingPage />} />
          <Route path="/features/medical-records" element={<MedicalRecordsFeature />} />
          <Route path="/features/ai-insights" element={<AiInsightsFeature />} />
          <Route path="/features/digital-twin" element={<DigitalTwinFeature />} />
          <Route path="/features/doctor-ai-assistant" element={<DoctorAiAssistantFeature />} />
          <Route path="/features/wearable-integration" element={<WearableIntegrationFeature />} />
          <Route path="/help" element={<HelpCenterPage />} />
          <Route path="/api-docs" element={<ApiDocsPage />} />
          <Route path="/doctor-signup" element={<DoctorSignup />} />
          <Route 
            path="/doctor-onboarding" 
            element={
              <DoctorOnboardingRoute>
                <DoctorOnboardingForm />
              </DoctorOnboardingRoute>
            } 
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/patient-onboarding"
            element={
              <PrivateRoute>
                <PatientForm />
              </PrivateRoute>
            }
          />
          <Route
            path="/doctor-dashboard"
            element={
              <DoctorPrivateRoute>
                <DoctorDashboard />
              </DoctorPrivateRoute>
            }
          />
          <Route
            path="/admin/login"
            element={<AdminLogin />}
          />
          <Route
            path="/admin-dashboard"
            element={<AdminDashboard />}
          />
          <Route
            path="/provider-dashboard"
            element={<ProviderDashboardSimple />}
          />
          <Route
            path="/book-appointment"
            element={
              <PrivateRoute>
                <AppointmentBooking />
              </PrivateRoute>
            }
          />
          <Route
            path="/doctors"
            element={
              <PrivateRoute>
                <DoctorList />
              </PrivateRoute>
            }
          />
        </Routes>
      </Suspense>
    </Router>
  );
}
